import { useState } from 'react';
import { X, Upload } from 'lucide-react';
import { useApiHandler } from '../../../utils/apiHandler';

function SuccessModal({ message, onClose }) {
  return (
    <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xs p-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-green-600">Success</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="mt-4 text-gray-700 text-center">{message}</p>
        <div className="mt-6 text-center">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function FailureModal({ message, onClose }) {
  return (
    <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xs p-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-red-600">Error</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="mt-4 text-gray-700 text-center">{message}</p>
        <div className="mt-6 text-center">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function UploadDocumentModal({ isOpen, onClose,onDocumentUpload }) {
  const [formData, setFormData] = useState({
    documentTitle: '',
    accessibleEmployees: 'all',
    departments: [],
    file: null,
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);

  const apiHandler = useApiHandler(); // Use the apiHandler hook

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append('documentTitle', formData.documentTitle);
    data.append('accessibleEmployees', formData.accessibleEmployees);
    if (formData.accessibleEmployees === 'departments') {
      data.append('departments', formData.departments.join(','));
    }
    data.append('file', formData.file);

    try {
      const response = await apiHandler.post('/admin/upload', data); // Use apiHandler for the POST request
      const result = response.data; // Get the response data from the API
      if (result.success) {
        setSuccessMessage('File uploaded successfully!');
        setShowSuccessModal(true);
        onDocumentUpload(result.data);

      } else {
        setErrorMessage(result.message || 'An error occurred.');
        setShowFailureModal(true);
      }
    } catch (error) {
      setErrorMessage('Something went wrong. Please try again.');
      setShowFailureModal(true);
    }

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div>
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-xl">
          <div className="flex justify-between items-center p-6 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Upload Document</h2>
              <p className="mt-1 text-sm text-gray-500">Share documents with your team</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8">
                <input
                  type="file"
                  id="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                />
                <label htmlFor="file" className="flex flex-col items-center justify-center cursor-pointer">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-700">
                    {formData.file ? formData.file.name : 'Click to upload or drag and drop'}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">PDF, Word, Excel (up to 10MB)</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Document Title</label>
                <input
                  type="text"
                  value={formData.documentTitle}
                  onChange={(e) => setFormData({ ...formData, documentTitle: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Access Level</label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="access"
                      value="all"
                      checked={formData.accessibleEmployees === 'all'}
                      onChange={(e) => setFormData({ ...formData, accessibleEmployees: e.target.value })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">All Employees</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="access"
                      value="departments"
                      checked={formData.accessibleEmployees === 'departments'}
                      onChange={(e) => setFormData({ ...formData, accessibleEmployees: e.target.value })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">Specific Departments</span>
                  </label>
                </div>

                {formData.accessibleEmployees === 'departments' && (
                  <div className="mt-3">
                    <select
                      multiple
                      value={formData.departments}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          departments: Array.from(e.target.selectedOptions, (option) => option.value),
                        })
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                    >
                      <option value="Engineering">Engineering</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Sales">Sales</option>
                      <option value="HR">HR</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Upload Document
              </button>
            </div>
          </form>
        </div>
      </div>

      {showSuccessModal && <SuccessModal message={successMessage} onClose={() => setShowSuccessModal(false)} />}
      {showFailureModal && <FailureModal message={errorMessage} onClose={() => setShowFailureModal(false)} />}
    </div>
  );
}

export default UploadDocumentModal;
